/**
 * Location detection and currency utilities
 * Version: 1.0.2
 */

export interface LocationData {
  country: string;
  countryCode: string;
  currency: string;
  locale: string;
  region: string;
}

// Currency exchange rates (base: USD)
export const exchangeRates: Record<string, number> = {
  USD: 1,
  UGX: 3800, // Ugandan Shilling
  EUR: 0.92, // Euro
  NGN: 1550, // Nigerian Naira
  KES: 129, // Kenyan Shilling
  ZAR: 18.5, // South African Rand
};

// Country to currency mapping
export const countryToCurrency: Record<string, string> = {
  US: 'USD',
  UG: 'UGX',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  NG: 'NGN',
  KE: 'KES',
  ZA: 'ZAR',
  GB: 'EUR', // Use EUR for UK as default (can be changed to GBP)
  CA: 'USD', // Use USD for Canada
  AU: 'USD', // Use USD for Australia
};

// Country to locale mapping
export const countryToLocale: Record<string, string> = {
  US: 'en-US',
  UG: 'en-UG',
  NG: 'en-NG',
  KE: 'en-KE',
  ZA: 'en-ZA',
  GB: 'en-GB',
  DE: 'de-DE',
  FR: 'fr-FR',
  IT: 'it-IT',
  ES: 'es-ES',
  NL: 'nl-NL',
  BE: 'nl-BE',
  AT: 'de-AT',
  IE: 'en-IE',
  CA: 'en-CA',
  AU: 'en-AU',
};

// Region mapping
export const countryToRegion: Record<string, string> = {
  US: 'North America',
  CA: 'North America',
  UG: 'East Africa',
  KE: 'East Africa',
  NG: 'West Africa',
  ZA: 'Southern Africa',
  GB: 'Europe',
  DE: 'Europe',
  FR: 'Europe',
  IT: 'Europe',
  ES: 'Europe',
  NL: 'Europe',
  BE: 'Europe',
  AT: 'Europe',
  IE: 'Europe',
  AU: 'Oceania',
};

/**
 * Detect user location from browser
 * Uses backend API which gets Cloudflare CF-IP-Country header
 */
export async function detectLocation(): Promise<LocationData> {
  try {
    // Use backend API which uses Cloudflare CF-IP-Country header
    const response = await fetch('https://zurichat-api.sales-f51.workers.dev/api/location');
    const data = await response.json();
    
    const countryCode = data.countryCode || 'US';
    const currency = countryToCurrency[countryCode] || 'USD';
    const locale = countryToLocale[countryCode] || 'en-US';
    const region = countryToRegion[countryCode] || 'Global';
    
    return {
      country: data.countryName || 'United States',
      countryCode,
      currency,
      locale,
      region,
    };
  } catch (error) {
    // Fallback to default US location
    return {
      country: 'United States',
      countryCode: 'US',
      currency: 'USD',
      locale: 'en-US',
      region: 'North America',
    };
  }
}

/**
 * Convert price from USD to target currency
 */
export function convertPrice(priceUSD: number, targetCurrency: string): number {
  const rate = exchangeRates[targetCurrency] || 1;
  return Math.round(priceUSD * rate);
}

/**
 * Format price with currency symbol
 */
export function formatPrice(price: number, currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    UGX: 'UGX ',
    EUR: '€',
    NGN: '₦',
    KES: 'KES ',
    ZAR: 'R ',
  };
  
  const symbol = symbols[currency] || '$';
  
  // For high-value currencies (UGX, NGN, KES), format with commas
  if (['UGX', 'NGN', 'KES'].includes(currency)) {
    return `${symbol}${price.toLocaleString()}`;
  }
  
  return `${symbol}${price}`;
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    UGX: 'UGX ',
    EUR: '€',
    NGN: '₦',
    KES: 'KES ',
    ZAR: 'R ',
  };
  
  return symbols[currency] || '$';
}
